import type { DateTime } from "luxon";
import type { Nurse, NurseData, SpecificPreference } from "../models";
import { isOnPtoDuringWindow, type ShiftAvailabilityWindow } from "../shiftAvailability";
import { buildEmployeeStatusWarning, type EmployeeStatusWarningProps } from "./employeeStatusWarning";
import { isAgencyNurse } from "./utils";

/** The figures a warning reads from the nurse's Add Staff grid row — the same numbers the row shows. */
export type AddWarningFigures = Pick<NurseData, "dailyHours" | "overtimeHours" | "predictedCost">;

export type AddWarningContext = {
	window: ShiftAvailabilityWindow;
	/** Department `dayStart`, for the PTO test. */
	dayStart: string;
	/** A nurse's time off, from `useNurseTimeOff`. */
	timeOffOf: (nurseId: number) => SpecificPreference[];
	departmentId: string;
	/** The staffing day — employee status is day-grained. */
	staffingDate: DateTime;
	/** Actuals mode: a worked-time entry, not a scheduled add. */
	isActual: boolean;
	/** The long-day limit in hours (`chosenDepartment.longShiftDuration`, or the warning's own fallback). */
	longShiftLimitHours: number;
};

/** Which add warnings apply to one nurse — each `null` / `false` / `undefined` when it doesn't. */
export type AddWarnings = {
	/** On Leave / Suspended on the staffing day. */
	employeeStatus: EmployeeStatusWarningProps | undefined;
	isAgency: boolean;
	pto: boolean;
	/** Paid hours that day including this shift, when over the long-day limit. */
	longDayHours: number | null;
	/** Predicted overtime this shift adds, with its display cost. */
	overtime: { hours: number; cost: string } | null;
};

/**
 * THE rule for which warnings an add must confirm, shared by the single add (whose dialog cascade only
 * decides the order they're asked in) and the multi-add's combined review — so one add and a batch of the
 * same nurses always warn alike. Employee status applies to everyone; agency staff and actual (worked-time)
 * adds skip PTO, daily hours and overtime. PTO is the row badge's own window test.
 */
export const classifyAddWarnings = (nurse: Nurse, figures: AddWarningFigures | undefined, ctx: AddWarningContext): AddWarnings => {
	const employeeStatus = buildEmployeeStatusWarning(nurse, ctx.departmentId, ctx.staffingDate);
	const isAgency = isAgencyNurse(nurse.employmentType);
	if (ctx.isActual || isAgency) return { employeeStatus, isAgency, pto: false, longDayHours: null, overtime: null };
	// DEFAULT: no prediction (wages hidden / still loading) = no overtime or daily-hours warning, as the add always had.
	const dailyHours = figures?.dailyHours || 0;
	const overtimeHours = figures?.overtimeHours || 0;
	return {
		employeeStatus,
		isAgency,
		pto: isOnPtoDuringWindow(ctx.timeOffOf(nurse.id), ctx.window, ctx.dayStart),
		longDayHours: dailyHours > ctx.longShiftLimitHours ? dailyHours : null,
		// DEFAULT: "-" is the grid's own "no prediction" spelling for the cost.
		overtime: overtimeHours > 0 ? { hours: overtimeHours, cost: figures?.predictedCost ?? "-" } : null,
	};
};
